import { closeModal } from "app/slices/modalSlice";

import { useTranslation } from "react-i18next";
import { Button, ControlledSelectGray } from "components/Shared";
import { useDispatch } from "react-redux";
import { ILeadDraft } from "interfaces";

import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";
import { useDebounce } from "ahooks";
import {
  deriveIndustryFlag,
  useCheckClientRegistryQuery,
  useCreateLeadMutation,
} from "api/compliance/leads_api";

import { UserPlus, AlertTriangle, ShieldAlert } from "lucide-react";
import useErrorListener from "hooks/useErrorListener";

import { emailPattern } from "utils/numberUtils";
import { ClientRegistryMatch, LeadIndustryFlag } from "interfaces/enums";
import { ALL_INDUSTRIES } from "pages/leads/industries";
import {
  CLIENT_TYPE_OPTIONS,
  CORRIDOR_OPTIONS,
  COUNTERPARTIES_OPTIONS,
  FREQUENCY_OPTIONS,
  isBlockingMatch,
  PRODUCT_OPTIONS,
  REGISTRY_NOTICE,
  VOLUME_RANGE_OPTIONS,
} from "pages/leads/options";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export const CreateLeadModal = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { isValid },
  } = useForm<ILeadDraft>({
    mode: "all",
    defaultValues: {
      company_name: "",
      contact_name: "",
    },
  });

  const [mutate, { error, isLoading }] = useCreateLeadMutation();

  useErrorListener({ error });

  // Live compliance pre-screen hint as the broker picks an industry.
  const industryFlag = deriveIndustryFlag(watch("industry"));

  // Live duplicate check against the registered-client registry. The
  // backend answers with a boolean only — the owning broker is never
  // disclosed to the caller. The RFC (canonical id) is the exact-match
  // key; company/email are the fuzzy fallback for pre-RFC captures.
  const debouncedCompany = useDebounce(watch("company_name"), { wait: 400 });
  const debouncedEmail = useDebounce(watch("email"), { wait: 400 });
  const debouncedRfc = useDebounce(watch("rfc"), { wait: 400 });
  const registryCheckEnabled =
    (debouncedCompany?.trim().length ?? 0) >= 4 ||
    (debouncedEmail?.trim().length ?? 0) >= 5 ||
    (debouncedRfc?.trim().length ?? 0) >= 12;
  const { data: registry } = useCheckClientRegistryQuery(
    {
      company_name: debouncedCompany?.trim(),
      email: debouncedEmail?.trim(),
      rfc: debouncedRfc?.trim(),
    },
    { skip: !registryCheckEnabled },
  );
  const match = registryCheckEnabled ? registry?.match : undefined;
  const registryNotice =
    match && match !== ClientRegistryMatch.NONE
      ? REGISTRY_NOTICE[match]
      : undefined;
  const isBlocked = isBlockingMatch(match);

  const doSubmit = async (values: ILeadDraft) => {
    const result = await mutate(values);
    if (!("error" in result)) {
      toast.success(t("New lead created"));
      handleClose();
    }
  };

  return (
    <DialogContent
      className="max-h-[90vh] overflow-y-auto"
      // The combobox popovers/drawers portal outside the dialog, so their
      // clicks read as outside interaction and would dismiss the whole
      // form mid-entry. A long data-entry modal should only close
      // explicitly (X / Escape / submit) anyway.
      onInteractOutside={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>{t("New lead")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(doSubmit)} className="space-y-4">
        <span className="text-sm font-bold text-[#767373]">
          {t(
            "Capture the prospect. Qualification can be completed later from the lead card.",
          )}
        </span>
        <Input
          placeholder={t("Company Name")}
          {...register("company_name", { required: true })}
        />
        <Input
          placeholder={t("Contact Name")}
          {...register("contact_name", { required: true })}
        />
        <Input
          placeholder={t("Email")}
          {...register("email", { required: true, pattern: emailPattern })}
        />
        {registryNotice && (
          <div
            className={cn(
              "flex items-start gap-2 rounded-lg p-3 text-sm",
              registryNotice.blocking
                ? "bg-warning/20 text-warning-foreground"
                : "bg-muted text-muted-foreground",
            )}
          >
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">{t(registryNotice.title)}</p>
              {t(registryNotice.body)}
            </div>
          </div>
        )}
        <Input placeholder={t("Phone Number")} {...register("phone_number")} />
        {/* Optional at capture, required before READY — the RFC is the
            canonical client id across the whole flow (CRM → KYB → engine). */}
        <Input
          placeholder={t("RFC (Tax ID)")}
          {...register("rfc", {
            validate: (value) =>
              !value ||
              /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(
                value.replace(/[\s.-]/g, "").toUpperCase(),
              ) ||
              t("Invalid RFC"),
          })}
        />
        {/* Drives which KB document checklist gates this lead. */}
        <Controller
          name="client_type"
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a client type")}
              renderProps={renderProps}
              options={CLIENT_TYPE_OPTIONS}
            />
          )}
        />
        <Controller
          name="industry"
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select an industry")}
              renderProps={renderProps}
              options={ALL_INDUSTRIES}
            />
          )}
        />
        {industryFlag === LeadIndustryFlag.PROHIBITED && (
          <div className="flex items-start gap-2 rounded-lg bg-[#DC2626]/10 p-3 text-sm text-[#DC2626]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {t(
              "Prohibited industry. The lead will be flagged — onboarding requires a compliance exception via your manager.",
            )}
          </div>
        )}
        {industryFlag === LeadIndustryFlag.HIGH_RISK && (
          <div className="flex items-start gap-2 rounded-lg bg-warning/20 p-3 text-sm text-warning-foreground">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {t(
              "High-risk industry. Enhanced documentation will be requested during onboarding.",
            )}
          </div>
        )}
        <Controller
          name="product"
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a product")}
              renderProps={renderProps}
              options={PRODUCT_OPTIONS}
            />
          )}
        />
        <Controller
          name="corridors"
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              isMulti
              placeholder={t("Select corridors")}
              renderProps={renderProps}
              options={CORRIDOR_OPTIONS}
            />
          )}
        />
        {/* Perfil transaccional declarado — feeds the compliance engine's
            transactional profile for the client (declared_profile). */}
        <div className="space-y-3 rounded-lg border border-border/50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("Transactional profile")}
          </p>
          <Controller
            name="estimated_monthly_volume_range"
            control={control}
            render={(renderProps) => (
              <ControlledSelectGray
                placeholder={t("Monthly transaction volume")}
                renderProps={renderProps}
                options={VOLUME_RANGE_OPTIONS}
              />
            )}
          />
          <Controller
            name="operation_frequency"
            control={control}
            render={(renderProps) => (
              <ControlledSelectGray
                placeholder={t("Transaction frequency")}
                renderProps={renderProps}
                options={FREQUENCY_OPTIONS}
              />
            )}
          />
          <Controller
            name="expected_counterparties_range"
            control={control}
            render={(renderProps) => (
              <ControlledSelectGray
                placeholder={t("Number of counterparties (beneficiaries)")}
                renderProps={renderProps}
                options={COUNTERPARTIES_OPTIONS}
              />
            )}
          />
          <Input
            placeholder={t("Origin of funds")}
            {...register("funds_origin")}
          />
          <Input
            placeholder={t("Destination of funds")}
            {...register("funds_destination")}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              "This information will be used by Compliance to build the client's transactional profile.",
            )}
          </p>
        </div>
        <Input
          placeholder={t("Source (referral, outbound, event...)")}
          {...register("source")}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={!isValid || isLoading || isBlocked}
        >
          <UserPlus />
          {t("Create lead")}
        </Button>
      </form>
    </DialogContent>
  );
};

export default CreateLeadModal;
